import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { getOrgMembers } from 'src/app/PeopleMembers/redux/peopleMembers.actions'
import {
  selectPeopleMembers,
  selectPeopleMembersLoadingFromState,
} from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import PeopleBoxAndLinks, {
  PeopleFilters,
  PeopleFiltersBody,
} from 'src/components/PeopleBoxAndLinks/PeopleBoxAndLinks'
import { OrganizationRoles } from 'src/enums'
import {
  useBackgroundDispatch,
  useQueryParamArrayHandler,
  useSortable,
  useStringSetState,
} from 'src/hooks'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectMembersListModelsFromState } from 'src/redux/selectors/data/members.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

export interface PeopleInfiniteScrollProps {
  fetch: (filtersBody: PeopleFiltersBody, page?: number) => void
  page?: number
  loading: boolean
  complete: boolean
}

export interface PeopleMembersAnalyticsEventProperties {
  organizationRoles: string[]
  showDeactivatedUsers: boolean
}

const PeopleMembers: React.FC = () => {
  const dispatch = useBackgroundDispatch()
  const membersLoading = useSelector(selectPeopleMembersLoadingFromState)
  const peopleFullPaginatedData = useSelector(selectPeopleMembers)
  const membersListModels = useSelector(selectMembersListModelsFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)
  const currentUser = useSelector(selectUserListModelFromState)
  const queryParams = useSelector(selectQueryParams)
  // will use this in the next pr
  const { ORGANIZATION_USER_STATISTICS: _canViewUserStats } =
    useQueryUserFeatures([UserFeature.ORGANIZATION_USER_STATISTICS]).data || {}

  const [stateQueryParams, setStateQueryParams] = useState(queryParams)
  const [initialLoad, setInitialLoad] = useState(false)

  const fetchMembers = (
    filtersBody?: PeopleFiltersBody,
    page?: number,
  ): void => {
    if (organization) {
      dispatch(
        getOrgMembers(organization.id, {
          ...filtersBody,
          page: page || 0,
          count: 25,
          renderMetrics: true,
        }),
      )
    }
  }

  const peopleMembersInfiniteScrollProps: PeopleInfiniteScrollProps = {
    ...peopleFullPaginatedData,
    fetch: fetchMembers,
  }

  const initialFilterValues: PeopleFilters = useMemo(
    () => ({
      keyword: stateQueryParams.search || '',
      organizationRoles: useQueryParamArrayHandler(
        stateQueryParams.organizationRoles,
      ) as OrganizationRoles[],
      showDeactivatedUsers:
        stateQueryParams.showDeactivatedUsers === undefined
          ? false
          : stateQueryParams.showDeactivatedUsers === 'true',
      sortDescending: true,
    }),
    [],
  )

  const [keywordFilter, setKeywordFilter] = useState(
    initialFilterValues.keyword,
  )

  const {
    set: organizationRolesFilter,
    setSet: setOrganizationRoleFilter,
    handleSelection: handleOrganizationRoleSelection,
  } = useStringSetState(
    new Set<string>(initialFilterValues.organizationRoles) ?? new Set<string>(),
  )

  const [hideDeactivatedUsers, setHideDeactivatedUsers] = useState(
    !initialFilterValues.showDeactivatedUsers,
  )

  const { setSort, sortBy, sortDescending } = useSortable(
    initialFilterValues.sortBy,
    initialFilterValues.sortDescending,
  )

  const initialfiltersBody: PeopleFiltersBody = {
    search: keywordFilter ? `${keywordFilter}` : undefined,
    organizationRoles: organizationRolesFilter
      ? (Array.from(organizationRolesFilter) as OrganizationRoles[])
      : undefined,
    showDeactivatedUsers: !hideDeactivatedUsers,
    sortField: sortBy,
    sortDirection: sortDescending === false ? 'ASC' : 'DESC',
  }

  // initial load
  useEffect((): void => {
    if (Object.keys(membersListModels).length === 0 && organization) {
      dispatch(
        getOrgMembers(organization.id, {
          ...initialfiltersBody,
          page: 0,
          count: 25,
          renderMetrics: true,
        }),
      )
      setInitialLoad(true)
    }
  }, [])

  return organization?.memberListVisible ||
    currentUser?.hasOrgCardManagerPermissions ? (
    <>
      <Helmet>
        <title>{`Organization Members | ${appName}`}</title>
        <meta
          name="description"
          content={`Organization Members page of ${appName}`}
        />
      </Helmet>
      <PeopleBoxAndLinks
        peopleInfiniteScrollProps={peopleMembersInfiniteScrollProps}
        loading={membersLoading}
        showFilters
        stateQueryParams={stateQueryParams}
        setStateQueryParams={setStateQueryParams}
        keywordFilter={keywordFilter}
        setKeywordFilter={setKeywordFilter}
        organizationRolesFilter={organizationRolesFilter}
        setOrganizationRoleFilter={setOrganizationRoleFilter}
        handleOrganizationRoleSelection={handleOrganizationRoleSelection}
        hideDeactivatedUsers={hideDeactivatedUsers}
        setHideDeactivatedUsers={setHideDeactivatedUsers}
        setSort={setSort}
        sortBy={sortBy}
        sortDescending={sortDescending}
        initialLoad={initialLoad}
        setInitialLoad={setInitialLoad}
      />
    </>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default PeopleMembers
